import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Typography } from '@material-ui/core';
import { showAlert } from 'actions/app';
import { setBulkEmailStatus } from 'actions/bulkEmail';
import { cancelSequenceStep } from 'services/bulkEmail';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import { PreviewMetaRowsSections } from 'UI/components/molecules/PreviewBulkEmail/components/PreviewMetaRowsSections';
import BulkStatsDrawer from 'UI/components/organisms/BulkStatsDrawer';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';

import { FIRST_STEP_ORDINAL, SEQUENCE_STATUS_IDS } from '../../constants';
import { BulkEmailStepsDrawer } from '../BulkEmailStepsDrawer';

import { useSequencesPreviewStyles } from './BulkEmailSequencesPreview.styled';
import { BulkEmailSequencesPreviewItem } from './BulkEmailSequencesPreviewItem';

const { cancelStep: cancelDialogCopies } = strings.bulkEmails.dialogs.sequences;
const { mailMeta: MAIL_META_STRINGS } = strings.bulkEmails.preview;

const CANCEL_STEP_DIALOG_INITIAL_STATE = { key: null, open: false, stepId: null };

export const BulkEmailSequencesPreview = ({
  initialBulkConfig,
  initialBulkMetaRows,
  sequencesMeta
}) => {
  const classes = useSequencesPreviewStyles();
  const dispatch = useDispatch();
  const [uiState, setUiState] = useState({
    cancelDialog: CANCEL_STEP_DIALOG_INITIAL_STATE,
    editStepDrawer: {
      open: false,
      step: null
    },
    isLoading: false,
    statsDrawerId: null
  });

  const activeSteps = useMemo(() => {
    const createdOnDate = initialBulkMetaRows.props.firstSectionData.find(
      ({ title }) => title === MAIL_META_STRINGS.createdOn.title
    );
    const step1 = {
      key: FIRST_STEP_ORDINAL,
      stepId: initialBulkConfig?.id,
      sendingDate: createdOnDate?.render ?? null
    };

    const active = sequencesMeta.filter(({ status }) => status.id !== SEQUENCE_STATUS_IDS.canceled);
    const mappedActive = active.map(step => {
      const sendingOnDate = step.meta.find(
        ({ title }) => title === MAIL_META_STRINGS.sendingOn.title
      );

      return {
        key: step.key,
        sendingDate: sendingOnDate.render,
        stepId: step.stepId
      };
    });

    return [step1, ...mappedActive];
  }, [initialBulkConfig, initialBulkMetaRows, sequencesMeta]);

  const sectionsMetaRows = useMemo(
    () =>
      sequencesMeta
        ? [
            {
              key: FIRST_STEP_ORDINAL,
              bulkId: initialBulkConfig.id,
              metaRows: initialBulkMetaRows,
              shouldEnableMenuButton: false,
              shouldEnableStatsButton: true
            },
            ...sequencesMeta.map(({ html, meta, status, subject, ...rest }) => ({
              emailBody: html,
              metaRows: (
                <PreviewMetaRowsSections
                  firstSectionData={meta}
                  subject={subject}
                  emailBody={html}
                  status={status}
                />
              ),
              subject,
              status,
              ...rest
            }))
          ]
        : [],
    [initialBulkConfig.id, initialBulkMetaRows, sequencesMeta]
  );

  const togglers = useMemo(
    () => ({
      cancelStepDialog:
        ({ key, open, stepId }) =>
        () =>
          setUiState(prev => ({ ...prev, cancelDialog: { key, open, stepId } })),
      editStepDrawer:
        ({ step, open }) =>
        () =>
          setUiState(prev => ({ ...prev, editStepDrawer: { step, open } })),
      statsDrawer:
        ({ id }) =>
        () =>
          setUiState(prev => ({ ...prev, statsDrawerId: id }))
    }),
    []
  );

  const handleCancelStep = async () => {
    setUiState(prev => ({ ...prev, isLoading: true }));
    const { alert, success } = await cancelSequenceStep({
      bulkId: initialBulkConfig.id,
      stepId: uiState.cancelDialog.stepId
    });
    dispatch(showAlert(alert));

    if (success) {
      dispatch(setBulkEmailStatus({ reloadEmailList: true }));
      togglers.cancelStepDialog(CANCEL_STEP_DIALOG_INITIAL_STATE)();
    }

    setUiState(prev => ({ ...prev, isLoading: false }));
  };

  return (
    <>
      {sectionsMetaRows.map((section, index) => (
        <BulkEmailSequencesPreviewItem
          delayDays={section.delayDays}
          key={section.key}
          isCanceled={section.status?.id === SEQUENCE_STATUS_IDS.canceled}
          isLastElement={index < sectionsMetaRows.length - 1}
          onOpenCancelDialog={togglers.cancelStepDialog({
            key: section.key,
            open: true,
            stepId: section.stepId
          })}
          onOpenEditStepDrawer={togglers.editStepDrawer({
            step: {
              currentStep: index + 1,
              emailBody: section.emailBody,
              id: section.stepId,
              status: section.status,
              subject: section.subject,
              triggerDays: section.delayDays
            },
            open: true
          })}
          onOpenStatsDrawer={togglers.statsDrawer({ id: section.bulkId })}
          sectionKey={section.key}
          shouldEnableMenuButton={section.shouldEnableMenuButton}
          shouldEnableStatsButton={section.shouldEnableStatsButton}
        >
          {section.metaRows}
        </BulkEmailSequencesPreviewItem>
      ))}
      <When condition={uiState.statsDrawerId}>
        <BulkStatsDrawer
          bulkId={uiState.statsDrawerId}
          bulkTypeId={initialBulkConfig.scope.id}
          onClose={togglers.statsDrawer({ id: null })}
          userEmail={initialBulkConfig.userEmail}
        />
      </When>
      <When condition={uiState.editStepDrawer.open}>
        <BulkEmailStepsDrawer
          bulkConfig={initialBulkConfig}
          onClose={togglers.editStepDrawer({ step: null, open: false })}
          shouldEdit
          step={uiState.editStepDrawer.step}
        />
      </When>
      <When condition={uiState.cancelDialog.open}>
        <DecisionDialog
          {...cancelDialogCopies.mainContent}
          fetching={uiState.isLoading}
          withButtons="YesNo"
          onConfirm={handleCancelStep}
          onClose={togglers.cancelStepDialog(CANCEL_STEP_DIALOG_INITIAL_STATE)}
        >
          <Typography variant="body1">
            {strings.formatString(cancelDialogCopies.body.message, {
              ordinalNumber: uiState.cancelDialog.key
            })}
          </Typography>
          <br />
          {activeSteps.map(step => {
            const isCanceledStep = uiState.cancelDialog.stepId === step.stepId;
            const stepDateCopy = strings.formatString(cancelDialogCopies.body.dates.mainCopy, {
              canceled: isCanceledStep ? cancelDialogCopies.body.dates.fillers.canceled : '',
              ordinalNumber: step.key,
              sendingDate: step.sendingDate
            });

            return (
              <Typography
                key={step.key}
                variant="body1"
                className={isCanceledStep ? classes.strikedText : null}
              >
                {stepDateCopy}
              </Typography>
            );
          })}
          <br />
          <Typography variant="body1">{cancelDialogCopies.body.question}</Typography>
        </DecisionDialog>
      </When>
    </>
  );
};
